import {useEffect, useMemo, useState} from 'react'
import {
  AlertCircle,
  BellRing,
  CheckCircle2,
  Clock3,
  Database,
  Loader,
  Phone,
  Play,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import WhatsAppContactModal from './WhatsAppContactModal'
import ManagementDeleteModal from './ManagementDeleteModal'
import {
  getTableSchemaAlertSettings,
  saveTableSchemaAlertContacts,
  triggerTableSchemaCheck,
} from '../services/managementService'
import type {TableSchemaAlertSettings, TableSchemaCheckReport} from '../services/managementService'

function formatPhoneForDisplay(phoneNumber: string): string {
  const digits = phoneNumber.replace(/\D/g, '')

  if (digits.startsWith('55') && digits.length >= 12) {
    return `+55 ${digits.slice(2, 4)} ${digits.slice(4, 9)}-${digits.slice(9)}`
  }

  return `+${digits}`
}

function formatCheckDate(value?: string): string {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Sao_Paulo',
  }).format(date)
}

function CheckResult({report}: {report: TableSchemaCheckReport}) {
  const changedTables = report.tables?.filter((table) => table.status === 'changed') || []
  const errorTables = report.tables?.filter((table) => table.status === 'error') || []

  return (
    <div className='space-y-4'>
      <div
        className={`rounded-lg border p-4 ${
          errorTables.length > 0
            ? 'border-red-200 bg-red-50'
            : changedTables.length > 0
            ? 'border-amber-200 bg-amber-50'
            : 'border-emerald-200 bg-emerald-50'
        }`}
      >
        <div className='flex items-start gap-3'>
          {errorTables.length > 0 ? (
            <AlertCircle className='mt-0.5 h-5 w-5 flex-shrink-0 text-red-600' />
          ) : changedTables.length > 0 ? (
            <BellRing className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600' />
          ) : (
            <CheckCircle2 className='mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600' />
          )}
          <div className='flex-1'>
            <p className='font-medium text-gray-900'>
              {errorTables.length > 0
                ? 'A checagem terminou com erro em uma ou mais tabelas.'
                : changedTables.length > 0
                ? `Alterações encontradas em ${changedTables.length} tabela(s).`
                : 'Nenhuma alteração encontrada.'}
            </p>
            <p className='mt-1 text-sm text-gray-600'>
              Última execução: {formatCheckDate(report.completedAt)}
              {' · '}
              {report.triggerType === 'automatic' ? 'Automática' : 'Manual'}
            </p>

            {changedTables.length > 0 && (
              <p className='mt-2 text-sm text-gray-700'>
                {report.notification?.sent
                  ? `Alerta enviado para ${report.notification.recipients} número(s).`
                  : report.notification?.error
                  ? 'As mudanças foram encontradas, mas o WhatsApp não pôde ser enviado.'
                  : report.notification?.reason === 'ALREADY_NOTIFIED'
                  ? 'Estas mesmas mudanças já tinham sido notificadas; nenhum novo alerta foi enviado.'
                  : 'As mudanças foram encontradas, mas não há números cadastrados para o envio.'}
              </p>
            )}
          </div>
        </div>
      </div>

      {changedTables.length > 0 && (
        <div className='grid gap-3 lg:grid-cols-2'>
          {changedTables.map((table) => (
            <div key={table.table} className='rounded-lg border border-gray-200 bg-white p-4'>
              <p className='font-medium text-gray-900'>{table.title}</p>
              <p className='mt-0.5 break-all text-xs text-gray-500'>{table.table}</p>

              <div className='mt-3 space-y-1 text-sm text-gray-700'>
                {table.possibleRenames?.map((rename) => (
                  <p key={`${rename.expected}-${rename.actual}`}>
                    Possível renomeação: {rename.expected} → {rename.actual}
                  </p>
                ))}
                {table.missing?.length > 0 && (
                  <p>Não encontradas: {table.missing.map((column) => column.name).join(', ')}</p>
                )}
                {table.added?.length > 0 && (
                  <p>Acrescentadas: {table.added.map((column) => column.name).join(', ')}</p>
                )}
                {table.caseChanges?.map((change) => (
                  <p key={`${change.expected}-${change.actual}`}>
                    Nome alterado: {change.expected} → {change.actual}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {errorTables.length > 0 && (
        <div className='rounded-lg border border-red-200 bg-white p-4'>
          <p className='font-medium text-red-800'>Tabelas não verificadas</p>
          <ul className='mt-2 list-disc space-y-1 pl-5 text-sm text-red-700'>
            {errorTables.map((table) => (
              <li key={table.table}>
                {table.title}: {table.error || 'Erro desconhecido'}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default function TableSchemaAlertsTab() {
  const [settings, setSettings] = useState<TableSchemaAlertSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isChecking, setIsChecking] = useState(false)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)
  const [phoneToDelete, setPhoneToDelete] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const contacts = settings?.contacts || []
  const lastCheck = settings?.lastCheck || null

  const sortedContacts = useMemo(
    () => [...contacts].sort((left, right) => left.localeCompare(right)),
    [contacts]
  )

  const fetchSettings = async () => {
    setIsLoading(true)
    setError(null)

    try {
      setSettings(await getTableSchemaAlertSettings())
    } catch (requestError) {
      console.error('Error loading table schema alerts:', requestError)
      setError('Não foi possível carregar a configuração dos alertas.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  const persistContacts = async (nextContacts: string[]) => {
    setIsSaving(true)
    setError(null)

    try {
      const savedContacts = await saveTableSchemaAlertContacts(nextContacts)
      setSettings((current) => (current ? {...current, contacts: savedContacts} : current))
      setIsContactModalOpen(false)
      setPhoneToDelete(null)
    } catch (requestError) {
      console.error('Error saving table schema alert contacts:', requestError)
      setError('Não foi possível salvar os números de WhatsApp.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddContact = async ({phoneNumber}: {phoneNumber: string}) => {
    if (contacts.includes(phoneNumber)) {
      setError('Esse número já está cadastrado.')
      setIsContactModalOpen(false)
      return
    }

    await persistContacts([...contacts, phoneNumber])
  }

  const handleManualCheck = async () => {
    setIsChecking(true)
    setError(null)

    try {
      const report = await triggerTableSchemaCheck()
      setSettings((current) => (current ? {...current, lastCheck: report} : current))
    } catch (requestError) {
      console.error('Error checking table schemas:', requestError)
      setError('Não foi possível executar a checagem das tabelas.')
    } finally {
      setIsChecking(false)
    }
  }

  if (isLoading) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <Loader className='mb-4 h-8 w-8 animate-spin text-violet-600' />
        <p className='text-gray-600'>Carregando alertas de tabelas...</p>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      <div className='rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
          <div className='flex items-start gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-violet-100'>
              <Database className='h-5 w-5 text-violet-600' />
            </div>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>Alterações nas tabelas</h2>
              <p className='text-sm text-gray-500'>
                Verifica diariamente as tabelas usadas pelo Compra AI e avisa pelo WhatsApp
              </p>
              <p className='mt-2 flex items-center gap-1.5 text-xs text-gray-500'>
                <Clock3 className='h-3.5 w-3.5' />
                Execução automática todos os dias às {settings?.schedule.time || '09:00'} no horário
                de São Paulo
              </p>
            </div>
          </div>

          <button
            type='button'
            onClick={handleManualCheck}
            disabled={isChecking}
            className='inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2 font-medium text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {isChecking ? (
              <Loader className='h-4 w-4 animate-spin' />
            ) : (
              <Play className='h-4 w-4' />
            )}
            <span>{isChecking ? 'Verificando...' : 'Verificar agora'}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className='flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4'>
          <AlertCircle className='mt-0.5 h-5 w-5 flex-shrink-0 text-red-600' />
          <p className='flex-1 text-sm text-red-700'>{error}</p>
          <button type='button' onClick={fetchSettings} className='text-red-600 hover:text-red-800'>
            <RefreshCw className='h-4 w-4' />
          </button>
        </div>
      )}

      {lastCheck && <CheckResult report={lastCheck} />}

      <div className='rounded-lg border border-gray-200 bg-white shadow-sm'>
        <div className='flex flex-col gap-3 border-b border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <h3 className='font-semibold text-gray-900'>Destinatários do alerta</h3>
            <p className='text-sm text-gray-500'>
              Todos os números abaixo recebem o alerta quando houver mudanças
            </p>
          </div>
          <button
            type='button'
            onClick={() => setIsContactModalOpen(true)}
            disabled={isSaving}
            className='inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-60'
          >
            <Plus className='h-4 w-4' />
            <span>Novo número</span>
          </button>
        </div>

        {sortedContacts.length === 0 ? (
          <div className='p-10 text-center'>
            <BellRing className='mx-auto mb-3 h-10 w-10 text-gray-300' />
            <p className='font-medium text-gray-900'>Nenhum número cadastrado</p>
            <p className='mt-1 text-sm text-gray-500'>
              A checagem continuará funcionando, mas nenhum alerta será enviado.
            </p>
          </div>
        ) : (
          <div className='divide-y divide-gray-200'>
            {sortedContacts.map((phone) => (
              <div
                key={phone}
                className='flex items-center justify-between gap-4 p-4 hover:bg-gray-50'
              >
                <div className='flex items-center gap-3'>
                  <div className='flex h-9 w-9 items-center justify-center rounded-full bg-green-100'>
                    <Phone className='h-4 w-4 text-green-600' />
                  </div>
                  <span className='text-sm font-medium text-gray-900'>
                    {formatPhoneForDisplay(phone)}
                  </span>
                </div>
                <button
                  type='button'
                  onClick={() => setPhoneToDelete(phone)}
                  disabled={isSaving}
                  className='rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 disabled:opacity-50'
                  title='Excluir'
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className='border-t border-gray-200 px-5 py-3 text-sm text-gray-500'>
          {contacts.length} {contacts.length === 1 ? 'número cadastrado' : 'números cadastrados'}
        </div>
      </div>

      <WhatsAppContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        onSave={handleAddContact}
        isSubmitting={isSaving}
      />

      <ManagementDeleteModal
        isOpen={Boolean(phoneToDelete)}
        onClose={() => setPhoneToDelete(null)}
        onConfirm={() => {
          if (phoneToDelete) {
            persistContacts(contacts.filter((phone) => phone !== phoneToDelete))
          }
        }}
        itemName={phoneToDelete ? formatPhoneForDisplay(phoneToDelete) : ''}
        itemType='contato'
        isSubmitting={isSaving}
      />
    </div>
  )
}
